import type { CheckBilateralTradeCsvResponse } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_service_pb'
import { useTranslation } from 'react-i18next'

import {
  CsvDiffSummary,
  type CsvDiffSummaryItem,
} from '~/components/common/CsvUpload/CsvDiffSummary'

/** Step2 のクリック式フィルタ (AC-DUC-012)。'all' = 全行 (初期選択)。 */
export type PreviewFilter =
  | 'all'
  | 'diff'
  | 'unchanged'
  | 'new'
  | 'updated'
  | 'deleted'
  | 'error'

type Props = {
  checkResult: CheckBilateralTradeCsvResponse
  activeFilter: PreviewFilter
  onFilterChange: (filter: PreviewFilter) => void
}

/**
 * Step2 のクリック式フィルタカード定義 (AC-DUC-012 / AC-DUC-013)。件数は
 * すべて `checkResult` (backend 計算値) から表示する — frontend で行を
 * 数え直さない。
 *
 * 並びは design (`admin/uploads/bilateral-trade.html` `#summary`) の
 * 「新規 / 変更 / 変更なし / 削除 / エラー / 差分のみ / 処理対象合計」。
 * 0 件の扱いは spec に従い、 エラーだけを 0 件で隠す (`common.md` §行レベル
 * バリデーション「エラー件数 0 のときは非表示」)。 変更なし / 新規 / 変更 / 削除は
 * AC-DUC-013 が常時表示を求めるので 0 件でも出す (design は 0 件で隠すが spec を優先)。
 *
 * 末尾の「処理対象合計」 は全行を出すフィルタで、 件数は 変更なし + 新規 + 変更 +
 * 削除 (エラー行は含めない。 `common.md`「『合計』件数表示は『処理対象合計』」)。
 */
function buildFilterCards(
  t: (key: string) => string,
  checkResult: CheckBilateralTradeCsvResponse,
): Array<CsvDiffSummaryItem<PreviewFilter>> {
  const processedRowCount =
    checkResult.unchangedCount +
    checkResult.createdCount +
    checkResult.updatedCount +
    checkResult.deletedCount
  const diffRowCount =
    checkResult.createdCount +
    checkResult.updatedCount +
    checkResult.deletedCount

  return [
    {
      filter: 'new',
      label: t('page:bilateralTradeCsvUpload.step2.summary.new'),
      count: checkResult.createdCount,
      tone: 'positive',
    },
    {
      filter: 'updated',
      label: t('page:bilateralTradeCsvUpload.step2.summary.updated'),
      count: checkResult.updatedCount,
      tone: 'warning',
    },
    {
      filter: 'unchanged',
      label: t('page:bilateralTradeCsvUpload.step2.summary.unchanged'),
      count: checkResult.unchangedCount,
    },
    {
      filter: 'deleted',
      label: t('page:bilateralTradeCsvUpload.step2.summary.deleted'),
      count: checkResult.deletedCount,
      tone: 'negative',
    },
    ...(checkResult.errorCount > 0
      ? [
          {
            filter: 'error' as const,
            label: t('page:bilateralTradeCsvUpload.step2.summary.error'),
            count: checkResult.errorCount,
            tone: 'negative' as const,
          },
        ]
      : []),
    {
      filter: 'diff',
      label: t('page:bilateralTradeCsvUpload.step2.summary.diff'),
      count: diffRowCount,
    },
    {
      filter: 'all',
      label: t('page:bilateralTradeCsvUpload.step2.summary.total'),
      count: processedRowCount,
    },
  ]
}

/**
 * Step2 件数サマリ = クリック式フィルタカード (AC-DUC-012 / AC-DUC-013)。
 * 件数は `checkResult` (backend 計算値) を表示するだけで、frontend で行を
 * 数え直さない。 見た目は CSV アップロード共通の `CsvDiffSummary` が持つ。
 */
export const FilterCards = ({
  checkResult,
  activeFilter,
  onFilterChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <CsvDiffSummary
      activeFilter={activeFilter}
      items={buildFilterCards(t, checkResult)}
      onFilterChange={onFilterChange}
    />
  )
}
